import type { BasketRepoError } from "@chatfood/dbe-client/errors";
import { useToast } from "@chatfood/design-system";
import { useLocale } from "@/composables/use-locale";
import { useNavigation, withoutItemQuery } from "@/composables/use-navigation";
import { useBasketStore } from "@/stores/basket";
import { useMenusStore } from "@/stores/menus";
import { useOutletStore } from "@/stores/outlet";
import { useModalHubStore } from "@/components/modal-hub";
import { normalizeErrorCode } from "@/utils/error-code";
import { useBugReporter } from "#imports";

const MENU_NOT_FOUND = "menu_not_found";
const BROWSE_ROUTES = new Set(["outlet", "menu"]);

export const useMenuNotFoundRecovery = () => {
  const basketStore = useBasketStore();
  const menusStore = useMenusStore();
  const outletStore = useOutletStore();
  const modalHub = useModalHubStore();
  const { t } = useLocale();
  const { toastUnexpectedError } = useToast();
  const { report } = useBugReporter();
  const { route, replace, rawReplace } = useNavigation();

  const isMenuNotFound = (error: BasketRepoError<"businessRule">) =>
    !!error.meta?.violation &&
    normalizeErrorCode(error.meta.violation) === MENU_NOT_FOUND;

  const refreshMenus = () => menusStore.refresh().catch(report);

  const staysOnBrowseRoute = () => {
    const name = String(route.value.name);

    if (!BROWSE_ROUTES.has(name)) return false;
    if (name !== "menu") return true;

    return menusStore.isMenuAvailable(
      String(route.value.params.menuSlug ?? ""),
    );
  };

  const closeItemModal = () => {
    const query = withoutItemQuery(route.value.query);

    if (Object.keys(query).length !== Object.keys(route.value.query).length) {
      rawReplace({ query });
    }
  };

  const leaveStaleScreens = () => {
    if (staysOnBrowseRoute()) return;

    replace(
      "outlet",
      {
        orderingMode: basketStore.orderingMode,
        outletSlug: outletStore.slug,
      },
      withoutItemQuery(route.value.query),
    );
  };

  const startAgain = async () => {
    modalHub.setWarningConfirmLoading();

    try {
      await Promise.all([refreshMenus(), basketStore.clearItems()]);
      leaveStaleScreens();
    } catch {
      toastUnexpectedError();
    } finally {
      modalHub.hideWarning();
    }
  };

  const tryHandleMenuNotFound = (
    error: BasketRepoError<"businessRule">,
  ): boolean => {
    if (!isMenuNotFound(error) || !basketStore.isLoaded) return false;

    closeItemModal();
    modalHub.hideLoading();
    modalHub.showWarning({
      title: t("composables.use_menu_not_found_recovery.title"),
      message: t("composables.use_menu_not_found_recovery.message"),
      confirmLabel: t("composables.use_menu_not_found_recovery.confirm_label"),
      onWarningConfirm: startAgain,
    });

    return true;
  };

  return { tryHandleMenuNotFound };
};
